import { z } from 'zod';
import { REMEDIATION_MODES, REMEDIATION_STEPS } from '../lib/remediationLadder.js';
/**
 * Payload shape for `GET /api/dashboard/anomalies`: the dashboard widget (five
 * rows) and the /anomalies page (`?limit=all`).
 *
 * Warren, 28 Oct 2026, predictive maintenance addendum item 3: "'reboots in
 * last 24h' counts reconnects, not BootNotifications. Every Merced unit shows
 * 10 to 12, which is our own Ocpp rolls. Show real reboots and reconnects
 * separately, leave out drops within 10 minutes of our own roll or crash and
 * network events, and group by site so a whole site down is one row. Add to
 * each row the last automatic action and its result, and who we're waiting on
 * (building, installer, EVI)." And item 2: "Show start success rate on the
 * charger page and the anomalies page ... Target 98% successful starts per
 * site; below that is an anomaly."
 *
 * A finding on one charger is one of:
 *   faulted            a connector in 'Faulted'
 *   offline            Down by the offline alert's rule (no contact for 15
 *                      minutes with the socket closed, or an open socket
 *                      silent for an hour)
 *   reboot_loop        more than 3 real reboots in 24 hours: boots our own
 *                      TriggerMessage or Reset caused, boots within 10
 *                      minutes of our roll or crash or a network event, and a
 *                      model's boot on every reconnect are not reboots
 *   unstable_link      3 or more counted reconnects in 24 hours (the quiet
 *                      alerts' UNSTABLE_DROPS), the same exclusions. It
 *                      counts every new socket the Ocpp task watch sees,
 *                      so it can list chargers the network health digest
 *                      does not (that counts only drops a 5-minute check
 *                      saw offline)
 *   error_spike        more than 5 CALLERRORs from the charger in an hour
 *   start_success_low  a site (or a charger with no site) below the 98%
 *                      target by ruling 3 of session success
 *   site_regression    a site worse than its own baseline on one signal
 *                      (start attempts a day, completion, never started, cut
 *                      mid-charge, energy per session): the daily
 *                      regression watch's open anomaly (Warren, 29 Oct 2026,
 *                      item 7; apps/api lib/regressionWatch), one line per
 *                      signal
 * Findings are grouped by site: one finding is its own row; two or more at a
 * site are one row with the lines under it; every live charger at a site of
 * two or more down together is one site_down row. Chargers that never
 * connected to v3 are their own list (never_connected), and chargers that are
 * not live (switched off, a switched-off site, commissioning, a maintenance
 * window) are only counted.
 *
 * 'unstable_boot' is no longer sent; it stays in the enum for one release so
 * an Admin bundle cached from before still reads the payload.
 *
 * Fields only EVI staff receive (the route strips them for site and partner
 * callers): boots.triggered/reset_by_us/ours/reconnect_boots,
 * reconnects_24h.total/ours/network, platform_note, handler_errors_1h,
 * platform_events, start_failures_network and watch_since. Everyone in scope
 * sees the counts that matter to the site, the last automatic action and who
 * we are waiting on.
 */
export const AnomalyTypeSchema = z.enum([
    'faulted',
    'offline',
    'unstable_boot',
    'error_spike',
    'reboot_loop',
    'unstable_link',
    'site_down',
    'start_success_low',
    'never_connected',
    'site_regression',
    'charger_traffic_regression',
]);
export const AnomalySeveritySchema = z.enum(['critical', 'warning']);
/** Who the row is waiting on. EVI covers the automatic fixes, our own side and nobody yet. */
export const AnomalyWaitingPartySchema = z.enum(['building', 'installer', 'evi']);
/**
 * asked         the building was asked to cycle the breaker (ladder step d)
 * service_call  an open service call (step e, or the service call scan)
 * automatic     the automatic fixes acted in the last 6 hours and it has not cleared
 * our_side      it dropped in our own roll or crash and has not come back (EVI only)
 * nobody_yet    nothing is open on it: EVI ops
 */
export const AnomalyWaitingDetailSchema = z.enum(['asked', 'service_call', 'automatic', 'our_side', 'nobody_yet']);
export const AnomalyWaitingOnSchema = z.object({
    party: AnomalyWaitingPartySchema,
    detail: AnomalyWaitingDetailSchema.nullable(),
    /** For a site row: how many of its lines wait on this party, and how many lines there are. */
    of: z.object({ count: z.number().int().nonnegative(), total: z.number().int().nonnegative() }).nullable().optional(),
});
/** The newest row the remediation ladder wrote that is an action (lib/remediation/latest.ts). */
export const AnomalyLastActionSchema = z.object({
    step: z.enum(REMEDIATION_STEPS),
    mode: z.enum(REMEDIATION_MODES),
    command: z.string().nullable(),
    outcome: z.string(),
    cleared: z.boolean().nullable(),
    at: z.string(),
    /** On a site row: the charger it was sent to. */
    charger_identity: z.string().nullable().optional(),
});
/** Incoming BootNotification CALLs in the last 24 hours, each in one class. */
export const AnomalyBootsSchema = z.object({
    /** Real reboots: everything the classes below do not explain. */
    reboots: z.number().int().nonnegative(),
    /** This model sends a BootNotification on every reconnect, so a boot is a reconnect unless something shows a reboot. */
    on_every_reconnect: z.boolean(),
    last_reboot_at: z.string().nullable(),
    /** EVI only. */
    total: z.number().int().nonnegative().optional(),
    triggered: z.number().int().nonnegative().optional(),
    reset_by_us: z.number().int().nonnegative().optional(),
    ours: z.number().int().nonnegative().optional(),
    reconnect_boots: z.number().int().nonnegative().optional(),
});
/** Reconnects in the last 24 hours (a reboot's own reconnect is a reboot, not a reconnect). */
export const AnomalyReconnectsSchema = z.object({
    /** Not ours, not in a network event, not after our reset: what counts against the charger. */
    counted: z.number().int().nonnegative(),
    /** EVI only. */
    total: z.number().int().nonnegative().optional(),
    ours: z.number().int().nonnegative().optional(),
    network: z.number().int().nonnegative().optional(),
});
/** Start success over the last 7 days (lib/sessionOutcomes/rollup.ts startSuccessRollup). */
export const AnomalyStartSuccessSchema = z.object({
    rate: z.number().min(0).max(1).nullable(),
    counted: z.number().int().nonnegative(),
    succeeded: z.number().int().nonnegative(),
    failed: z.number().int().nonnegative(),
    window_30d: z.object({ counted: z.number().int().nonnegative(), failed: z.number().int().nonnegative(), rate: z.number().min(0).max(1).nullable() }),
    below_target: z.boolean(),
    critical: z.boolean(),
    completion_rate: z.number().min(0).max(1).nullable(),
    /** The counted failures' causes, most first (at most three). A Site persona reads driver-side causes as one. */
    top_causes: z.array(z.object({ cause: z.string(), count: z.number().int().nonnegative() })),
});
/** One finding on one charger: a line under a site row, or a row of its own. */
export const AnomalyLineSchema = z.object({
    charger_id: z.string(),
    charger_identity: z.string(),
    anomaly_type: AnomalyTypeSchema,
    severity: AnomalySeveritySchema,
    duration_ms: z.number().int().nonnegative(),
    description: z.string(),
    /** Null when nothing was ever seen (a never-connected charger). */
    last_seen: z.string().nullable(),
    /** EVI only: "Dropped in our Ocpp crash at 00:57Z and has not reconnected". */
    platform_note: z.string().nullable().optional(),
});
export const AnomalyRowSchema = z.object({
    /** A charger row: the charger. A site row: its lead line's charger, so a bundle from before group rows still links somewhere real. */
    charger_id: z.string(),
    charger_identity: z.string(),
    /** May be null when a charger has no assigned location yet. */
    location_id: z.string().nullable(),
    location_name: z.string().nullable(),
    anomaly_type: AnomalyTypeSchema,
    severity: AnomalySeveritySchema,
    /** Milliseconds since the anomaly showed up: since last contact for 'offline', since the connector's status for
     *  'faulted', since the whole site was down for 'site_down', the window itself (24h, 1h, 7d) for the counts. */
    duration_ms: z.number().int().nonnegative(),
    /** Human-readable description: "Offline for 47m", "5 reboots in 24h", "All 3 chargers offline since 22:44Z". */
    description: z.string(),
    /** Most recent timestamp tied to this anomaly. Since 29 Oct 2026 never the registration date of a charger never seen. */
    last_seen: z.string(),
    /** 'site:<location id>' or 'charger:<charger id>': the row's key. */
    group_key: z.string().optional(),
    /** 'site': the lines are under it. 'charger': one charger, one finding. */
    scope: z.enum(['charger', 'site']).optional(),
    /** Chargers behind the row. */
    charger_count: z.number().int().nonnegative().optional(),
    /** Live chargers in the caller's scope at the site. */
    site_charger_count: z.number().int().nonnegative().optional(),
    /** "All N of your chargers here": the caller sees only some of the site's chargers, so no site down is claimed. */
    assignment_scope: z.boolean().optional(),
    lines: z.array(AnomalyLineSchema).optional(),
    boots: AnomalyBootsSchema.nullable().optional(),
    reconnects_24h: AnomalyReconnectsSchema.nullable().optional(),
    start_success: AnomalyStartSuccessSchema.nullable().optional(),
    last_action: AnomalyLastActionSchema.nullable().optional(),
    waiting_on: AnomalyWaitingOnSchema.nullable().optional(),
    /** EVI only. */
    platform_note: z.string().nullable().optional(),
    /** EVI only: our own CALLERROR replies to this charger in the last hour (an EVI handler bug, not the charger). */
    handler_errors_1h: z.number().int().nonnegative().optional(),
});
/** A charger registered on v3 that has never connected to it. */
export const NeverConnectedRowSchema = z.object({
    charger_id: z.string(),
    charger_identity: z.string(),
    display_name: z.string().nullable(),
    location_id: z.string().nullable(),
    location_name: z.string().nullable(),
    registered_at: z.string().nullable(),
    last_action: AnomalyLastActionSchema.nullable(),
    waiting_on: AnomalyWaitingOnSchema,
});
/**
 * Our own roll, crash or scale-in (the Api's Ocpp task watch, lib/ocppTaskWatch.ts), or many sites reconnecting at
 * once: 'network' once the watch ran and saw no task end near it, 'wave' before the watch (ours or the network, not
 * told apart). Drops within 10 minutes of one are not counted against a charger. EVI only.
 */
export const PlatformEventKindSchema = z.enum(['roll', 'crash', 'scale_in', 'unknown', 'network', 'wave']);
export const PlatformEventViewSchema = z.object({
    kind: PlatformEventKindSchema,
    started_at: z.string(),
    ended_at: z.string(),
    /** Chargers on the task when it ended, or chargers that reconnected in the wave. */
    chargers: z.number().int().nonnegative(),
    sites: z.number().int().nonnegative(),
});
export const DashboardAnomaliesResponseSchema = z.object({
    /** Bounded preview list (default 5): top-level rows, a site group being one. */
    rows: z.array(AnomalyRowSchema),
    /** Every top-level row in the caller's scope; used by the "View all N" footer link in the widget. */
    total_count: z.number().int().nonnegative(),
    /**
     * The caller is not EVI staff (Warren, 4 Nov 2026: "CHARGER ALERTS GO TO EVI ONLY"): the rows are empty because this list is
     * EVI's, not because nothing is wrong, so the Admin never says "all chargers operating normally" on it.
     */
    evi_only: z.boolean().optional(),
    /** Server-side timestamp when the cache window opened. Drives the "as of <time>" tooltip on the widget header. */
    generated_at: z.string(),
    /** Chargers behind all the rows. */
    charger_count: z.number().int().nonnegative().optional(),
    never_connected: z.array(NeverConnectedRowSchema).optional(),
    /** Chargers left out because they are not live: switched off, at a switched-off site, commissioning, in a maintenance window. */
    not_live_count: z.number().int().nonnegative().optional(),
    /** EVI only: the last 24 hours. */
    platform_events: z.array(PlatformEventViewSchema).optional(),
    /**
     * EVI only: start failures and cut sessions from our own roll or crash across the scope, carried once instead of
     * raising each site. failed is the starts only (round 5: it used to fold in the cut sessions, printed as failed
     * starts); sessions_failed the sessions it cut.
     */
    start_failures_network: z.object({
        failed: z.number().int().nonnegative(),
        sessions_failed: z.number().int().nonnegative().optional(),
        sites: z.number().int().nonnegative(),
        chargers: z.number().int().nonnegative(),
        first_at: z.string().nullable(),
        last_at: z.string().nullable(),
    }).nullable().optional(),
    /**
     * EVI only: when the Ocpp task watch's current run began, null when it is not polling now. Whenever it was not
     * polling (before it, switched off, a gap), reconnects come from the 5-minute offline check.
     */
    watch_since: z.string().nullable().optional(),
    /** EVI only: the watch's last poll, null when it never ran. */
    watch_last_at: z.string().nullable().optional(),
});
//# sourceMappingURL=dashboard-anomalies.js.map